<template>
    <div>
        <div class="position-relative">
            <input
                :id="id"
                v-model="modelInput"
                :type="config?.type ?? 'text'"
                :placeholder="config?.placeholder ?? ''"
                class="form-control"
                :class="{
                    [config.classCustom]: config.classCustom,
                    'is-invalid': config?.error ?? false,
                    disabled_cursor: disabled
                }"
                :style="styleInput ?? ''"
                :name="config?.name ?? ''"
                :maxlength="config?.maxlength ?? null"
                :min="config?.min ?? ''"
                :max="config?.max ?? ''"
                :multiple="config?.multiple ?? false"
                :autofocus="config?.autofocus ?? false"
                :disabled="disabled"
                :autocomplete="config?.autocomplete ?? 'off'"
                :size="config?.size"
                @input="handleInput"
                @change="handleChangeInput"
                @keypress="handleKeyPress"
                @blur="handleBlur"
                @paste="handlePaste"
                @compositionstart="isComposing = true"
                @compositionend="handleCompositionEnd"
            />
            <div v-if="config?.type === 'color' && !modelInput" class="color-fake"></div>
        </div>
        <div v-if="config.error">
            <MessageInput :configMessage="config" />
        </div>
    </div>
</template>

<script>
import { isNumber, convertToHalfsize } from '@/utils/format';

import MessageInput from './message.vue';

export default {
    name: 'InputCommon',
    components: {
        MessageInput
    },
    props: {
        id: {
            type: String,
            required: false,
            default: 'input'
        },
        styleInput: {
            type: [String, Object],
            required: false,
            default: null
        },
        model: {
            required: true
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        config: {
            type: Object,
            required: true,
            default() {
                return {
                    classCustom: 'form-control',
                    name: '',
                    maxlength: null,
                    type: 'text',
                    placeholder: '',
                    min: '',
                    max: '',
                    isRequired: false,
                    isMaxLength: false,
                    isKatakana: false,
                    isUrl: false,
                    isEmail: false,
                    isNumber: false,
                    isConvertHalfsize: false,
                    isPassword: false,
                    multiple: false,
                    autofocus: false,
                    error: false,
                    isTrim: false,
                    errorCustomMess: '',
                    valueSync: '',
                    size: '',
                    typesValidate: []
                };
            }
        }
    },
    data() {
        return {
            modelInput: '',
            isComposing: false
        };
    },
    watch: {
        '$props.model': {
            handler: function (newValue) {
                let config = this.config;
                config.valueSync = newValue;
                this.modelInput = newValue;
            },
            immediate: true
        },

        '$props.config': {
            handler: function (newConfig) {
                this.config = newConfig;
                this.$emit('update:config', this.config);
            },
            immediate: true,
            deep: true
        },

        modelInput: {
            handler: function (newModelInput) {
                let config = this.config;
                config.error = false;
                config.isRequired = false;
                config.isMaxLength = false;
                config.isKatakana = false;
                config.isUrl = false;
                config.isEmail = false;
                config.isPassword = false;
                config.errorCustomMess = '';
                config.valueSync = newModelInput;
                this.$emit('update:model', newModelInput);
                this.$emit('update:config', config);
            }
        }
    },
    methods: {
        handleCompositionEnd(event) {
            this.isComposing = false;
            this.handleInput(event);
        },
        handleInput(event) {
            let config = this.config;
            if (this.isComposing) return;

            let value = event.target.value;
            if (config.isNumber || config.isConvertHalfsize) {
                value = convertToHalfsize(value);
            }

            if (config.isNumber) {
                if (config.allowNegative) {
                    // Allow temporary minus sign or minus followed by digits
                    const isNegative = value.startsWith('-');
                    let numericPart = value.replace(/[^\d]/g, '');
                    value = isNegative ? '-' + numericPart : numericPart;
                } else {
                    value = value.replace(/[^\d]/g, '');
                }
            }

            // Force update matching target value to prevent non-numeric/full-width characters lingering in UI
            event.target.value = value;
            this.modelInput = value;
            this.$emit('update:model', value);
            this.$emit('input', value);
        },
        handleChangeInput() {
            let config = this.config;
            let value = this.modelInput;

            if (config.isNumber || config.isConvertHalfsize) {
                value = convertToHalfsize(value ? value.toString() : '');
            }

            if (config.isNumber) {
                if (config.allowNegative) {
                    const isNegative = value.startsWith('-');
                    let numericPart = value.replace(/[^\d]/g, '');
                    value = isNegative ? '-' + numericPart : numericPart;
                } else {
                    value = value.replace(/[^\d]/g, '');
                }
            }

            const isPasswordField = config.typesValidate?.includes('password') || config.type === 'password' || config.isPassword;
            if (this.config?.isTrim && typeof value === 'string' && !isPasswordField) {
                value = value.trim();
            }

            this.modelInput = value;

            this.$emit('update:model', this.modelInput);
            this.$emit('change', this.modelInput);
        },
        handleKeyPress(event) {
            let config = this.config;
            if (config.isNumber) {
                if (config.allowNegative && event.key === '-') {
                    // Allow minus sign only at the beginning and if not already present
                    if (this.modelInput.toString().indexOf('-') === -1 && event.target.selectionStart === 0) {
                        return;
                    }
                }
                isNumber(event);
            }
            this.$emit('update:model', this.modelInput);
            this.$emit('keypress', this.modelInput);
        },
        handleBlur() {
            const isPasswordField = this.config?.typesValidate?.includes('password') || this.config?.type === 'password' || this.config?.isPassword;
            if (this.config?.isTrim && typeof this.modelInput === 'string' && !isPasswordField) {
                this.modelInput = this.modelInput.trim();
                this.$emit('update:model', this.modelInput);
            }
            this.$emit('blur');
        },
        handlePaste() {
            this.$emit('paste');
        }
    }
};
</script>

<style lang="scss" scoped>
.color-fake {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #ffffff;
    border: 1px solid #ccc;
    pointer-events: none;
    z-index: 1;
}
</style>
